// DTO : Data Transformer Object
import type { BusinessUnit } from '@backend/lib-l10n';

import { getJobsBusinessUnitUrl, getJobsUrl } from '~/config';
import { businessUnits } from '~/config/businessUnits';

export const getGlobalJsonld = (businessUnit: BusinessUnit, extraJsonLd?: Record<string, unknown>) => ({
  '@context': 'https://schema.org',
  '@graph': [getWebsiteJsonld(businessUnit), getCorporationJsonld(businessUnit), extraJsonLd].filter(Boolean),
});

/**
 * Generate Website JSON-LD
 * schema.org WebSite documentation : https://schema.org/WebSite
 * google json-ld format testing : https://search.google.com/test/rich-results
 */
export const getWebsiteJsonld = (businessUnit: BusinessUnit): Record<string, any> => ({
  '@type': 'WebSite',
  name: businessUnits[businessUnit].isProman ? businessUnits[businessUnit].brandName : 'iziwork jobs',
  url: getJobsBusinessUnitUrl(businessUnit),
});

/**
 * Generate Corporation JSON-LD
 * schema.org Corporation documentation : https://schema.org/Corporation
 * google json-ld format testing : https://search.google.com/test/rich-results
 */
export const getCorporationJsonld = (businessUnit: BusinessUnit): Record<string, any> => {
  const { brandName, isProman } = businessUnits[businessUnit];

  // the social accounts and the logo below are iziwork's: a Proman site would credit them to its own brand
  if (isProman) {
    return { '@type': 'Corporation', name: brandName, url: getJobsBusinessUnitUrl(businessUnit) };
  }

  const links = getSocialMediaLinks(businessUnit);

  return {
    '@type': 'Corporation',
    name: 'iziwork jobs',
    alternateName: 'iziwork',
    url: links.website,
    logo: `${getJobsUrl()}/images/logo-iziwork-by-proman.png`,
    // the profiles of the brand elsewhere: the site itself is `url`
    sameAs: [links.twitter, links.facebook, links.instagram, links.linkedin, links.youtube],
  };
};

export const getSocialMediaLinks = (businessUnit: string) => ({
  twitter: `https://twitter.com/iziwork${businessUnit}`,
  facebook: `https://www.facebook.com/Iziwork${businessUnit}/`,
  instagram: `https://www.instagram.com/iziwork_${businessUnit.toLowerCase()}/`,
  linkedin: 'https://www.linkedin.com/company/iziwork/',
  website: getJobsBusinessUnitUrl(businessUnit),
  youtube: 'https://www.youtube.com/channel/UCJoPiK8A5c7B9QYv2obQzTQ',
});
